/*
 * The owner's Obsidian vault drawn as a small universe on black. Each note is a star. Hubs (notes with the most links) are
 * the brightest stars and sit near the core; notes with a fair amount under them are semi bright; loose notes are faint
 * dust out on the rim. Each folder has its own star color, so folders show as clusters. The whole thing turns slowly.
 *
 * The firing is real, not random: notes edited lately fire on their own, the newest most often (an hour old, every few
 * seconds; two days old, now and then; older, only when a signal reaches them). A note edited since the last copy fires a
 * big burst when it arrives. A signal runs down a curved link, adds charge to the next star, and that star fires once it
 * has enough, then rests a moment before it can fire again. Stars twinkle a little; that is the only made-up motion.
 * Hover a star and the turning slows, everything else dims, the star rings and its links light up toward the notes it
 * links to, and its name sits right beside it. Light look: the same universe in ink on a pale sky. Stops drawing when off
 * screen or the tab is hidden; with reduced motion it is one still picture.
 */
import { useEffect, useRef, useState } from "react";
import type { VaultGraphAnswer } from "@steward/shared";
import { unzoom } from "../app/fit-wide";

const HOUR = 3_600_000;

/** How the card is painted: night sky in the dark look, ink on a pale sky in the light look. Colors are r,g,b. */
interface Look {
  light: boolean;
  /** Star color per folder. */
  colors: Record<string, string>;
  other: string;
  /** A firing star's flash; in the light look a star flashes in its own color. */
  hot: string | null;
  dust: string;
  /** How strong glows are drawn (ink on white needs less). */
  halo: number;
}
const DARK: Look = {
  light: false,
  colors: {
    "steward-platform": "150,200,255",
    steward: "120,160,255",
    rules: "255,140,200",
    business: "120,240,230",
    marketing: "255,214,110",
    websites: "130,240,150",
    research: "200,160,255",
    logos: "255,120,120",
    root: "235,240,255",
  },
  other: "210,215,240",
  hot: "235,245,255",
  dust: "200,210,255",
  halo: 0.55,
};
const LIGHT: Look = {
  light: true,
  colors: {
    "steward-platform": "37,99,235",
    steward: "79,70,229",
    rules: "219,39,119",
    business: "13,148,136",
    marketing: "202,138,4",
    websites: "22,163,74",
    research: "124,58,237",
    logos: "220,38,38",
    root: "51,65,85",
  },
  other: "100,116,139",
  hot: null,
  dust: "90,100,140",
  halo: 0.4,
};
/** Folder names as the owner reads them in the hover card. */
const FOLDER_NAMES: Record<string, string> = {
  "steward-platform": "Steward Platform",
  steward: "Steward",
  rules: "Rules",
  business: "Business",
  marketing: "Marketing",
  websites: "Websites",
  research: "Research",
  logos: "Logos",
  root: "Top of the vault",
};

function lightLook(): boolean {
  const t = document.documentElement.dataset.theme;
  return t === "light" || (t !== "dark" && !window.matchMedia("(prefers-color-scheme: dark)").matches);
}
/** How far the camera sits, in layout units past the edge. */
const CAMERA = 4;

interface Star {
  x: number;
  y: number;
  z: number;
  /** 0 a loose note, 1 the biggest hub. */
  weight: number;
  group: string;
  color: string;
  label: string;
  edited: number;
  links: number[];
  /** Charge from incoming signals; fires at 1. Leaks away. */
  charge: number;
  /** How lit it is, 1 just fired, 0 at rest. */
  glow: number;
  /** Resting after firing until this time (ms, performance clock). */
  rest: number;
  /** Times a minute it fires on its own, from how lately it was edited. */
  rate: number;
  twinkle: number;
}

interface Signal {
  from: number;
  to: number;
  start: number;
  ms: number;
  strength: number;
}

/** A tiny fixed random from a number, so the picture is the same every time. */
const hash = (n: number) => {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
};

/**
 * Stars push apart, links pull together, and each star is held near its own distance from the core: hubs in the middle,
 * loose notes on the rim. Worked out once in 3D, the same every time.
 */
function layout(graph: VaultGraphAnswer, now: number): Star[] {
  const n = graph.nodes.length;
  const links = graph.nodes.map((): number[] => []);
  for (const [a, b] of graph.links) {
    links[a]?.push(b);
    links[b]?.push(a);
  }
  const most = Math.max(1, ...links.map((l) => l.length));
  const weight = links.map((l) => Math.log(1 + l.length) / Math.log(1 + most));
  const R = 70;
  const aim = weight.map((w) => R * (0.08 + 0.92 * Math.pow(1 - w, 1.3)));
  const pos = graph.nodes.map((_, i) => {
    const y = 1 - (2 * (i + 0.5)) / n;
    const ring = Math.sqrt(1 - y * y);
    const turn = i * 2.399963;
    const d = aim[i]!;
    return { x: Math.cos(turn) * ring * d, y: y * d, z: Math.sin(turn) * ring * d, vx: 0, vy: 0, vz: 0 };
  });
  for (let tick = 0, alpha = 1; tick < 260; tick++, alpha *= 0.985) {
    for (let i = 0; i < n; i++) {
      const a = pos[i]!;
      for (let j = i + 1; j < n; j++) {
        const b = pos[j]!;
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dz = a.z - b.z;
        const d2 = Math.max(dx * dx + dy * dy + dz * dz, 0.5);
        if (d2 > 2500) continue;
        const f = (900 * alpha) / (d2 * Math.sqrt(d2));
        a.vx += dx * f;
        a.vy += dy * f;
        a.vz += dz * f;
        b.vx -= dx * f;
        b.vy -= dy * f;
        b.vz -= dz * f;
      }
    }
    for (const [ai, bi] of graph.links) {
      const a = pos[ai]!;
      const b = pos[bi]!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dz = b.z - a.z;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
      const f = ((d - 18) / d) * 0.012 * alpha;
      a.vx += dx * f;
      a.vy += dy * f;
      a.vz += dz * f;
      b.vx -= dx * f;
      b.vy -= dy * f;
      b.vz -= dz * f;
    }
    pos.forEach((p, i) => {
      const d = Math.hypot(p.x, p.y, p.z) || 1;
      const pull = ((aim[i]! - d) / d) * 0.12 * alpha; // back toward its own distance from the core
      p.vx += p.x * pull;
      p.vy += p.y * pull;
      p.vz += p.z * pull;
      p.x += p.vx;
      p.y += p.vy;
      p.z += p.vz;
      p.vx *= 0.55;
      p.vy *= 0.55;
      p.vz *= 0.55;
    });
  }
  const radius = Math.max(...pos.map((p) => Math.hypot(p.x, p.y, p.z)), 1);
  return graph.nodes.map((g, i) => {
    const p = pos[i]!;
    const age = g.edited ? (now - g.edited) / HOUR : Infinity;
    return {
      x: p.x / radius,
      y: p.y / radius,
      z: p.z / radius,
      weight: weight[i]!,
      group: g.group,
      color: DARK.colors[g.group] ?? DARK.other,
      label: g.label,
      edited: g.edited ?? 0,
      links: links[i] ?? [],
      charge: 0,
      glow: 0,
      rest: 0,
      rate: age < 1 ? 14 : age < 6 ? 5 : age < 24 ? 2 : age < 48 ? 0.8 : 0,
      twinkle: hash(i) * Math.PI * 2,
    };
  });
}

/** Far stars behind the notes, out past the rim, so it reads as space. */
function dust(count: number): { x: number; y: number; z: number; a: number }[] {
  return Array.from({ length: count }, (_, i) => {
    const y = hash(i + 0.31) * 2 - 1;
    const ring = Math.sqrt(1 - y * y);
    const turn = hash(i + 0.77) * Math.PI * 2;
    const d = 0.55 + Math.pow(hash(i + 0.13), 0.6) * 0.75;
    return { x: Math.cos(turn) * ring * d, y: y * d, z: Math.sin(turn) * ring * d, a: 0.15 + hash(i + 0.5) * 0.4 };
  });
}

/** A soft round glow in a color, drawn once and scaled for every star (much cheaper than a gradient per star). */
function glowSprite(color: string): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const g = c.getContext("2d");
  if (!g) return c;
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, `rgba(${color},1)`);
  grad.addColorStop(0.18, `rgba(${color},0.45)`);
  grad.addColorStop(0.5, `rgba(${color},0.1)`);
  grad.addColorStop(1, `rgba(${color},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  return c;
}

/** "edited 5 min ago" for the hover name. */
function editedWords(ms: number): string {
  const mins = Math.max(1, Math.round((Date.now() - ms) / 60000));
  if (mins < 60) return `edited ${String(mins)} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `edited ${String(hours)} h ago`;
  return `edited ${String(Math.round(hours / 24))} days ago`;
}

/** A fixed bend for each link, so links curve a little and never wiggle. */
const bend = (a: number, b: number) => (hash(a * 1000 + b) - 0.5) * 0.3;

/** Shortest the card gets when it fills the rest of the screen, and tallest (a phone scrolls past it). */
const FILL_MIN = 360;
const FILL_MAX = 900;

/** The box that scrolls the page: the nearest parent that scrolls, else the page itself. */
function scrollerOf(el: HTMLElement): HTMLElement {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const y = getComputedStyle(p).overflowY;
    if (y === "auto" || y === "scroll") return p;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

/** height: a fixed height, or none to fill from the card down to the bottom of the screen with no page scroll. */
export function VaultBrain({ graph, height }: { graph: VaultGraphAnswer; height?: number }) {
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  /** When each note was edited in the copy shown before, so a new edit fires a burst when the next copy arrives. */
  const before = useRef<Map<string, number> | null>(null);
  const tip = useRef<HTMLDivElement>(null);
  /** The hovered note for the card beside it; kept after the pointer leaves so the card can fade out. */
  const [hover, setHover] = useState<{
    label: string;
    folder: string;
    color: string;
    links: number;
    edited: number;
    on: boolean;
  } | null>(null);

  useEffect(() => {
    const box = wrap.current;
    const el = canvas.current;
    const card = tip.current;
    const ctx = el?.getContext("2d");
    if (!box || !el || !card || !ctx) return;
    const stars = layout(graph, Date.now());
    const far = dust(600);
    const sprites = new Map<string, HTMLCanvasElement>();
    const sprite = (color: string) => {
      let s = sprites.get(color);
      if (!s) sprites.set(color, (s = glowSprite(color)));
      return s;
    };
    const signals: Signal[] = [];
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const shown = stars.map(() => ({ x: 0, y: 0, s: 1, depth: 1 }));
    let width = 0;
    let h = height ?? FILL_MIN;
    let fitX = 1;
    let fitY = 1;
    /** Stars grow a little on a big card (1 on a 240 tall card). */
    let zoom = 1;
    let seen = true;
    let frame = 0;
    let hovered = -1;
    /** The star the hover look is on: the hovered one, or the one just left while the look fades. */
    let focused = -1;
    /** 0 no hover look, 1 full; eases both ways. */
    let focus = 0;
    let near = new Set<number>();
    let last = performance.now();
    /** The turning's own clock: it runs slower while a star is hovered, so the star holds still under the pointer. */
    let clock = last;
    let spin = { ct: 1, st: 0, cl: 1, sl: 0 };
    let look = DARK;
    let sky: CanvasGradient | string = "#000";

    const paint = () => {
      look = lightLook() ? LIGHT : DARK;
      for (const p of stars) p.color = look.colors[p.group] ?? look.other;
      if (look.light) {
        const g = ctx.createRadialGradient(width / 2, h / 2, 0, width / 2, h / 2, Math.max(width, h) * 0.6);
        g.addColorStop(0, "#ffffff");
        g.addColorStop(1, "#eef1f8");
        sky = g;
      } else sky = "#000";
    };
    const flash = (p: Star) => look.hot ?? p.color;

    const size = () => {
      width = box.clientWidth;
      if (height === undefined) {
        const top = box.getBoundingClientRect().top + window.scrollY;
        h = Math.round(Math.min(FILL_MAX, Math.max(FILL_MIN, unzoom(window.innerHeight - top) - 16)));
        box.style.height = `${String(h)}px`;
        // Whatever still spills past the screen (page padding, margins) comes off the card, so nothing scrolls.
        const page = scrollerOf(box);
        const over = page.scrollHeight - page.clientHeight;
        if (over > 0) {
          h = Math.max(FILL_MIN, h - over);
          box.style.height = `${String(h)}px`;
        }
      }
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      el.width = Math.round(width * dpr);
      el.height = Math.round(h * dpr);
      el.style.width = `${String(width)}px`;
      el.style.height = `${String(h)}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Wider than tall, like a galaxy seen at a slant, and never past the card at its nearest.
      fitY = (h / 2 - 4) * ((CAMERA - 1) / CAMERA);
      zoom = Math.min(2, Math.max(1, Math.sqrt(h / 240)));
      fitX = Math.min((width / 2 - 8) * ((CAMERA - 1) / CAMERA), fitY * 1.9);
      paint();
    };

    /** Turn slowly (one turn about every three minutes, a slight rocking tilt) and project onto the card. */
    const point = (x0: number, y0: number, z0: number) => {
      const x = x0 * spin.ct + z0 * spin.st;
      const z1 = -x0 * spin.st + z0 * spin.ct;
      const y = y0 * spin.cl - z1 * spin.sl;
      const z = y0 * spin.sl + z1 * spin.cl;
      const s = CAMERA / (CAMERA + z);
      return { x: width / 2 + x * fitX * s, y: h / 2 + y * fitY * s, s, depth: Math.min(1, Math.max(0.3, 0.65 - z * 0.4)) };
    };
    const project = (t: number) => {
      const turn = t / 29000;
      const tilt = 0.35 + Math.sin(t / 31000) * 0.1;
      spin = { ct: Math.cos(turn), st: Math.sin(turn), cl: Math.cos(tilt), sl: Math.sin(tilt) };
      stars.forEach((p, i) => Object.assign(shown[i]!, point(p.x, p.y, p.z)));
    };

    /** A star fires: a flash, then signals down a few of its links (more when the burst is strong), then a short rest. */
    const fire = (i: number, strength: number, t: number) => {
      const p = stars[i];
      if (!p || t < p.rest) return;
      p.glow = Math.max(p.glow, Math.min(1, 0.4 + strength * 0.6));
      p.charge = 0;
      p.rest = t + 700;
      if (strength < 0.22) return;
      const send = Math.min(p.links.length, 1 + Math.floor(Math.random() * (1 + strength * 3)));
      const picks = [...p.links].sort(() => Math.random() - 0.5).slice(0, send);
      for (const j of picks) {
        if (signals.length > 70) break;
        const a = shown[i]!;
        const b = shown[j]!;
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        signals.push({ from: i, to: j, start: t + 40 + Math.random() * 140, ms: 420 + d * 12, strength: strength * 0.74 });
      }
    };

    /** Where a signal is on its curved link: done 0 at the start, 1 at the end. */
    const along = (s: Signal, done: number): [number, number] => {
      const lo = Math.min(s.from, s.to);
      const hi = Math.max(s.from, s.to);
      const a = shown[lo]!;
      const b = shown[hi]!;
      const k = bend(lo, hi);
      const cx = (a.x + b.x) / 2 - (b.y - a.y) * k;
      const cy = (a.y + b.y) / 2 + (b.x - a.x) * k;
      const u = s.from === lo ? done : 1 - done;
      const v = 1 - u;
      return [v * v * a.x + 2 * v * u * cx + u * u * b.x, v * v * a.y + 2 * v * u * cy + u * u * b.y];
    };

    /** Lays a link's curve as a path, ready to stroke. */
    const curve = (i: number, j: number) => {
      const a = shown[i]!;
      const b = shown[j]!;
      const k = bend(Math.min(i, j), Math.max(i, j));
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.quadraticCurveTo((a.x + b.x) / 2 - (b.y - a.y) * k, (a.y + b.y) / 2 + (b.x - a.x) * k, b.x, b.y);
    };

    /** How lit a star stays while another is hovered: the hovered star and the notes it links to stay, the rest dims. */
    const kept = (i: number) => (i === focused || near.has(i) ? 1 : 1 - focus * 0.78);

    const draw = (t: number) => {
      project(clock);
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, width, h);

      // Far dust.
      const dim = (1 - focus * 0.6) * (look.light ? 0.55 : 1);
      const speck = look.light ? 1 : 0.8;
      for (const [k, d] of far.entries()) {
        const o = point(d.x, d.y, d.z);
        ctx.fillStyle = `rgba(${look.dust},${String(d.a * o.depth * (0.75 + 0.25 * Math.sin(t / 900 + k)) * dim)})`;
        ctx.fillRect(o.x, o.y, speck, speck);
      }

      // Links: very faint threads in the star's color, a little brighter where either end is lit.
      ctx.lineWidth = look.light ? 0.6 : 0.45;
      const fade = 1 - focus * 0.85;
      stars.forEach((p, i) => {
        for (const j of p.links) {
          if (j < i || (focus > 0.01 && (i === focused || j === focused))) continue; // the hovered star's are drawn lit below
          const a = shown[i]!;
          const b = shown[j]!;
          const q = stars[j]!;
          const lit = Math.max(p.glow, q.glow);
          const color = p.weight >= q.weight ? p.color : q.color;
          const base = (0.03 + Math.max(p.weight, q.weight) * 0.06) * (look.light ? 1.6 : 1);
          ctx.strokeStyle = `rgba(${color},${String((base + lit * 0.3) * ((a.depth + b.depth) / 2) * fade)})`;
          curve(i, j);
          ctx.stroke();
        }
      });

      // The hovered star's links: lit threads with a flow running out toward the notes it links to.
      if (focused >= 0 && focus > 0.01) {
        const p = stars[focused]!;
        for (const j of p.links) {
          ctx.lineWidth = 1.1;
          ctx.strokeStyle = `rgba(${p.color},${String(0.35 * focus)})`;
          curve(focused, j);
          ctx.stroke();
          ctx.lineWidth = 1.6;
          ctx.setLineDash([2, 7]);
          ctx.lineDashOffset = still ? 0 : -t / 30;
          ctx.strokeStyle = `rgba(${stars[j]!.color},${String(0.85 * focus)})`;
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }

      ctx.globalCompositeOperation = look.light ? "source-over" : "lighter";
      // Signals: a bright head with a fading tail, running down the curve.
      for (let n = signals.length - 1; n >= 0; n--) {
        const s = signals[n]!;
        const done = (t - s.start) / s.ms;
        if (done < 0) continue;
        if (done >= 1) {
          signals.splice(n, 1);
          const q = stars[s.to]!;
          q.charge += s.strength * 1.6;
          q.glow = Math.max(q.glow, 0.15);
          if (q.charge >= 1) fire(s.to, Math.min(1, s.strength + 0.15), t);
          continue;
        }
        const color = stars[s.from]!.color;
        for (let k = 6; k >= 0; k--) {
          const at = done - k * 0.03;
          if (at < 0) continue;
          const [x, y] = along(s, at);
          const fade = 1 - k / 7;
          const keep = Math.min(kept(s.from), kept(s.to));
          ctx.fillStyle =
            k === 0
              ? `rgba(${look.hot ?? color},${String((0.6 + s.strength * 0.4) * keep)})`
              : `rgba(${color},${String(fade * 0.5 * (0.4 + s.strength) * keep)})`;
          ctx.beginPath();
          ctx.arc(x, y, k === 0 ? 1.2 : fade, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Stars, back to front: a glow that grows with weight (and with firing), then a small bright core.
      const order = stars.map((_, i) => i).sort((a, b) => shown[a]!.s - shown[b]!.s);
      for (const i of order) {
        const p = stars[i]!;
        const o = shown[i]!;
        const keep = kept(i);
        const mine = i === focused ? focus : 0;
        const linked = near.has(i) ? focus : 0;
        const tw = 0.85 + 0.15 * Math.sin(t / 700 + p.twinkle);
        const shine = (Math.pow(p.weight, 1.6) * 0.9 * tw + p.glow + linked * 0.35 + mine * 0.8) * keep;
        if (shine > 0.08) {
          const halo = (4 + p.weight * 14 + p.glow * 16 + linked * 5 + mine * 14) * o.s * zoom;
          ctx.globalAlpha = Math.min(1, shine * look.halo * o.depth);
          ctx.drawImage(sprite(p.glow > 0.3 ? flash(p) : p.color), o.x - halo, o.y - halo, halo * 2, halo * 2);
        }
        ctx.globalAlpha = 1;
        const resting = t < p.rest && p.glow < 0.3;
        const core = (0.7 + Math.pow(p.weight, 1.4) * 2.4) * o.s * zoom * (1 + p.glow * 0.5 + linked * 0.35 + mine * 0.9);
        const alpha = (0.4 + Math.pow(p.weight, 0.8) * 0.6) * tw * o.depth * (resting ? 0.5 : 1) * keep + linked * 0.4 + mine;
        const hub = !look.light && p.weight > 0.6;
        ctx.fillStyle =
          p.glow > 0.35 || mine > 0.5
            ? `rgba(${flash(p)},${String(keep)})`
            : `rgba(${hub ? flash(p) : p.color},${String(Math.min(1, alpha + p.glow * keep))})`;
        ctx.beginPath();
        ctx.arc(o.x, o.y, core, 0, Math.PI * 2);
        ctx.fill();
        if (mine > 0.01) ring(o.x, o.y, core, p.color, t);
      }
      ctx.globalCompositeOperation = "source-over";
      place();
    };

    /** The hovered star's ring: a steady ring that breathes, and a ripple that runs out from it now and then. */
    const ring = (x: number, y: number, core: number, color: string, t: number) => {
      ctx.globalCompositeOperation = "source-over";
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = `rgba(${color},${String(0.9 * focus)})`;
      ctx.beginPath();
      ctx.arc(x, y, core + 5 + (still ? 0 : Math.sin(t / 260) * 1.2) + (1 - focus) * 6, 0, Math.PI * 2);
      ctx.stroke();
      if (!still) {
        const phase = (t % 1400) / 1400;
        ctx.lineWidth = 1;
        ctx.strokeStyle = `rgba(${color},${String((1 - phase) * 0.55 * focus)})`;
        ctx.beginPath();
        ctx.arc(x, y, core + 5 + phase * 22, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.globalCompositeOperation = look.light ? "source-over" : "lighter";
    };

    /** Keeps the name card right beside the hovered star as it turns: above and to the right, flipped near an edge. */
    const place = () => {
      card.style.opacity = String(focus);
      if (focused < 0) return;
      const o = shown[focused]!;
      const gap = 12 + o.s * zoom * 3;
      const w = card.offsetWidth;
      const ch = card.offsetHeight;
      const left = o.x + gap + w > width - 8 ? o.x - gap - w : o.x + gap;
      const top = o.y - gap - ch < 8 ? o.y + gap : o.y - gap - ch;
      card.style.transform = `translate(${String(Math.round(left))}px, ${String(Math.round(top + (1 - focus) * 6))}px)`;
    };

    const loop = (t: number) => {
      frame = 0;
      const dt = Math.min(64, t - last);
      last = t;
      const leak = Math.pow(0.3, dt / 1000);
      const fade = Math.pow(0.14, dt / 1000);
      focus += ((hovered >= 0 ? 1 : 0) - focus) * (1 - Math.pow(0.002, dt / 1000));
      if (hovered < 0 && focus < 0.01) {
        focus = 0;
        focused = -1;
      }
      clock += dt * (1 - focus * 0.92);
      stars.forEach((p, i) => {
        p.charge *= leak;
        p.glow *= fade;
        if (p.rate && Math.random() < (p.rate / 60000) * dt) fire(i, 0.75 + Math.random() * 0.25, t);
      });
      draw(t);
      if (seen && !document.hidden) frame = requestAnimationFrame(loop);
    };
    const start = () => {
      if (still || frame || !seen || document.hidden) return;
      last = performance.now();
      frame = requestAnimationFrame(loop);
    };

    size();
    project(clock);
    // A note edited since the last copy fires hard. On the first copy, the newest note fires: the last thing worked on.
    const now = performance.now();
    const was = before.current;
    if (was) {
      stars.forEach((p, i) => {
        const prior = was.get(p.label);
        if (prior === undefined || p.edited > prior) fire(i, 1, now);
      });
    } else {
      const newest = stars.reduce((b, p, i) => (p.edited > (stars[b]?.edited ?? 0) ? i : b), 0);
      fire(newest, 1, now);
    }
    before.current = new Map(stars.map((p) => [p.label, p.edited]));
    draw(now);
    start();

    const resized = new ResizeObserver(() => {
      if (box.clientWidth === width) return; // its own height change, nothing to redo
      size();
      if (still || !frame) draw(performance.now());
    });
    resized.observe(box);
    const windowResized = () => {
      size();
      if (still || !frame) draw(performance.now());
    };
    window.addEventListener("resize", windowResized);
    const watch = new IntersectionObserver(([e]) => {
      seen = Boolean(e?.isIntersecting);
      start();
    });
    watch.observe(box);
    const tab = () => start();
    document.addEventListener("visibilitychange", tab);
    // The light or dark look follows Settings and the device.
    const relook = () => {
      paint();
      if (still || !frame) draw(performance.now());
    };
    const themeWatch = new MutationObserver(relook);
    themeWatch.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    const device = window.matchMedia("(prefers-color-scheme: dark)");
    device.addEventListener("change", relook);

    const move = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const mx = unzoom(e.clientX - rect.left);
      const my = unzoom(e.clientY - rect.top);
      let best = -1;
      let bestD = 196;
      shown.forEach((o, i) => {
        const d = (o.x - mx) ** 2 + (o.y - my) ** 2;
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      });
      if (best !== hovered) {
        hovered = best;
        const p = stars[best];
        if (p) {
          if (focused !== best) focus = Math.min(focus, 0.35); // a new star eases in from mostly off
          focused = best;
          near = new Set(p.links);
          setHover({
            label: p.label,
            folder: FOLDER_NAMES[p.group] ?? p.group,
            color: p.color,
            links: p.links.length,
            edited: p.edited,
            on: true,
          });
        } else setHover((was) => (was ? { ...was, on: false } : was));
        el.style.cursor = p ? "pointer" : "";
        if (still) {
          focus = p ? 1 : 0;
          if (!p) focused = -1;
          draw(performance.now());
        }
      }
    };
    const leave = () => {
      hovered = -1;
      el.style.cursor = "";
      setHover((was) => (was ? { ...was, on: false } : was));
      if (still) {
        focus = 0;
        focused = -1;
        draw(performance.now());
      }
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerleave", leave);

    return () => {
      cancelAnimationFrame(frame);
      resized.disconnect();
      window.removeEventListener("resize", windowResized);
      watch.disconnect();
      document.removeEventListener("visibilitychange", tab);
      themeWatch.disconnect();
      device.removeEventListener("change", relook);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerleave", leave);
    };
  }, [graph, height]);

  return (
    <div className="vault-brain" ref={wrap} style={{ height: height ?? FILL_MIN }}>
      <canvas
        ref={canvas}
        role="img"
        aria-label={`${String(graph.nodes.length)} notes as stars. The notes with the most links shine brightest; notes edited lately fire.`}
      />
      <div className="vault-brain-tip" ref={tip} aria-hidden={!hover?.on}>
        {hover ? (
          <>
            <span className="vault-brain-name">
              <i style={{ background: `rgb(${hover.color})` }} />
              {hover.label}
            </span>
            <span className="vault-brain-when">
              {hover.folder} · {hover.links === 1 ? "1 link" : `${String(hover.links)} links`}
              {hover.edited ? ` · ${editedWords(hover.edited)}` : null}
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
}
